// #region IMPORT
import type { AnimationClip, EulerOrder, Skeleton, SkinnedMesh } from 'three';
import { Quaternion, Vector3 } from 'three';
import { subscribeKey } from 'valtio/utils';

import { QUATERNIONS_BY_OPTION } from '@cartwhl/3d/bvh';
import RingQuads from '@cartwhl/3d/threejs/shapes/RingQuads';
import UnitAxesMesh from '@cartwhl/3d/threejs/shapes/UnitAxesMesh';
import { disposeObject3D, traverseFind } from '@cartwhl/3d/threejs/util.js';
import { poseFromSkeleton } from '@cartwhl/3d/threejs/utils/BVHExporter.js';

import { posingStore, sceneSettingsStore } from 'stores/index.js';
import { IKChainType } from 'stores/posingStore.js';

import { DEFAULT_IK_CHAIN, IK_SELECTORS } from '../constants.js';
import type PosingSystem from './PosingSystem.js';
// @ts-expect-error ts(7016) : Could not find a declaration file for module
import { IKPoseRig } from './ossos/IKPoseRig.js';
// @ts-expect-error ts(7016) : Could not find a declaration file for module
import { Armature, Transform } from './ossos/ossos.min.js';
import {
  applyPoseToSkeleton,
  armaturePoseFromClip,
  cloneSkeleton,
  skeletonToArmature,
} from './ossos/util';

// #endregion

// #region CONSTANTS

const ALLOW_POS_JOINTS = ['pelvis'];

const IS_DEV_ENV = process.env.NODE_ENV === 'development';

// #endregion

export default class Poser {
  // #region MAIN
  pSystem: PosingSystem; // Refence to parent system, mainly for access to gizmo
  skel!: Skeleton; // ThreeJS's Skeleton
  skelBind!: Skeleton; // Clone of the initial skeletion state
  arm!: Armature; // Library's Skeleton
  rig!: IKPoseRig; // Posing IK Rig
  parentTransform = new Transform(); // Keep track of original transform of skeleton's parent

  subscriptions: Array<() => void> = []; // Collection of unsubscribe callbacks

  selectors = new RigSelectors(this); // Manage Selector points
  targets = new RigTargets(this); // Manage Target Points

  // Load clip as a pose on next character load
  availableClip?: THREE.AnimationClip = undefined;
  availableClipFrame: number = 0;

  constructor(ps: PosingSystem) {
    this.pSystem = ps;

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ps.grpUI.add(this.selectors.mesh, this.targets.mesh);

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Events / Callbacks
    this.subscriptions.push(
      subscribeKey(
        posingStore.state,
        'activeIKChain',
        this.onActiveChainChange,
      ),

      subscribeKey(
        sceneSettingsStore.state.characterSettings,
        'staticHands',
        this.onStaticHand,
      ),
    );

    console.log('Poser.Init');
  }

  dispose() {
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // UnSubscibe
    for (const fn of this.subscriptions) fn();

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    this.selectors.dispose();
    this.targets.dispose();

    console.log('Poser.Dispose');
  }
  // #endregion

  // #region LOADING
  loadCharacter(mesh: SkinnedMesh): boolean {
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Reset character's skeleton before swopping it out
    if (this.skel && this.skelBind) {
      IS_DEV_ENV &&
        console.log(
          'Poser.loadCharacter - Reseting previous character skeleton',
        );

      for (let i = 0; i < this.skel.bones.length; i++) {
        this.skel.bones[i].quaternion.copy(this.skelBind.bones[i].quaternion);
        this.skel.bones[i].position.copy(this.skelBind.bones[i].position);
      }

      // Reset parent object back to original transform
      const p = this.skel.bones[0].parent;
      p?.position.fromArray(this.parentTransform.pos);
      p?.quaternion.fromArray(this.parentTransform.rot);
      p?.scale.fromArray(this.parentTransform.scl);
    }

    const swopPose: any = this.rig ? this.rig.wpose.clone() : null;

    posingStore.clearActiveIKChain();

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    this.arm = skeletonToArmature(mesh.skeleton);
    this.skelBind = cloneSkeleton(mesh.skeleton);
    this.skel = mesh.skeleton;

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Compute offset transform that sometimes exist
    const rootParent = this.skel.bones[0].parent;

    if (rootParent) {
      const q = rootParent.getWorldQuaternion(new Quaternion());
      const p = rootParent.getWorldPosition(new Vector3());
      const s = rootParent.getWorldScale(new Vector3());
      const pose = this.arm.bindPose;

      pose.offset.rot.copy(q.toArray());
      pose.offset.pos.copy(p.toArray());
      pose.offset.scl.copy(s.toArray());
      pose.updateWorld();
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    this.rig = new IKPoseRig(this.arm.bindPose, true);

    if (swopPose) {
      for (const b of swopPose.bones) {
        const wb = this.rig.wpose.getBone(b.name);
        wb.local.rot.copy(b.local.rot);

        if (ALLOW_POS_JOINTS.includes(b.name)) {
          wb.local.pos.copy(b.local.pos);
        }
      }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Apply 90 Degree to handle Zup to Yup Conversion

    // Set as offset to working pose for IK Rig
    this.rig.wpose.offset.rot.rotX(Math.PI * -0.5);

    // Swop pose should have already have modified first bone
    if (!swopPose) {
      // Character lays on it's back because its Yup oriented
      // Reverse rotation on the first bone along with the translation
      // so the character is not sinking below the floor.
      const bl = this.rig.wpose.bones[0].local;
      bl.rot.rotX(Math.PI * 0.5);
      bl.pos.copy([bl.pos[0], -bl.pos[2], bl.pos[1]]); // [x,-z,y] - X 90deg Shorthand
    }

    // -------------------
    // Backup parent transform data
    this.parentTransform.pos.copy(rootParent?.position.toArray());
    this.parentTransform.rot.copy(rootParent?.quaternion.toArray());
    this.parentTransform.scl.copy(rootParent?.scale.toArray());

    // -------------------
    // Apply 90 degree rotation to the parent object of the first bone
    // this will then act as an offset to the bone's rotation & translation
    IS_DEV_ENV &&
      console.log('Poser - Applying 90Deg rotation to character parent');
    const q = new Quaternion().setFromAxisAngle(
      new Vector3(1, 0, 0),
      Math.PI * -0.5,
    );
    rootParent?.quaternion.premultiply(q);

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    this.refreshSkeleton();
    this.rig.initTargets();
    this.selectors.render();

    // Push initial pose to a cleared history
    this.pSystem.poseToHistory(true);

    // Pre-select an IK chain after rig is available
    queueMicrotask(() => {
      posingStore.state.activeIKChain = DEFAULT_IK_CHAIN;
    });

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // RACE CONDITION - Bvh is ready before the character,
    // clip will be loaded in the next character then cleared out
    if (this.availableClip) {
      IS_DEV_ENV &&
        console.log('Clip was alaready available, setting pose from clip');
      this.setPoseFromClip(this.availableClip, this.availableClipFrame);
      this.availableClip = undefined;
    } else {
      IS_DEV_ENV &&
        console.log('Clip was not available, setting pose from clip');
    }

    IS_DEV_ENV && console.log('Poser - Auto Rigging Character Complete');
    return true;
  }
  // #endregion

  // #region SETTERS / GETTERS

  getPoseClone(): any {
    const pose = this.rig.wpose.clone();

    // HACK - Pose does not have twist properties
    // Storing twist in this manner is the
    // easiest way to handle pose history
    pose.twistA = this.getTwist(0);
    pose.twistB = this.getTwist(1);

    return pose;
  }

  setPoseFromClip(clip: AnimationClip, frame: number = 0): boolean {
    if (!this.rig || !this.rig.wpose) {
      IS_DEV_ENV &&
        console.log(
          'Rig not available, clip will be stored for next available character',
        );
      this.availableClip = clip;
      this.availableClipFrame = frame;
      return false;
    }

    // reset working pose to bind/T-pose first
    this.rig.wpose.copy(this.rig.tpose);

    // Deselect Active Chain
    posingStore.clearActiveIKChain();

    // Apply clip frame to working pose
    armaturePoseFromClip(this.rig.wpose, clip, frame, ALLOW_POS_JOINTS);

    this.refresh(true);

    // Run solvers to snap pose to IK targets to remove minor twitch
    this.rig.runSolvers();
    this.refreshSkeleton();
    this.selectors.render();

    // Some poses load outside of view, recenter camera around pose
    // this.pSystem.fitViewPose(this.rig.wpose);
    this.pSystem.poseToHistory(false, false);

    IS_DEV_ENV && console.log('Poser - Set pose from clip frame', frame);
    return true;
  }

  setPose(pose: any): this {
    this.rig.wpose.copy(pose);

    // HACK - Pose does not have twist properties
    // Data is appended during history saved, load it
    // in if its available else reset twists
    const twistA = pose.twistA || 0;
    const twistB = pose.twistB || 0;
    this.rig.spine.target.setStartTwist(twistA);
    this.rig.spine.target.setEndTwist(twistB);

    this.refresh(true);
    return this;
  }

  setFKPos(v: Array<number>): this {
    const pose = this.rig.wpose;

    const selKey = posingStore.state.activeIKChain; // Which IK Selector?
    const sel = IK_SELECTORS[selKey]; // Get Selector Info
    const ch = this.rig[sel.chainKey].chain; // Get IK Chain for the selector
    const b = pose.bones[ch.links[0].index]; // Get the first bone of the chain in the working pose

    // Convert world space position to local space
    const ptran = pose.getWorldTransform(b.pindex);
    ptran.pos
      .fromSub(v, ptran.pos)
      .transformQuat(ptran.rot.invert())
      .mul(ptran.scl.invert());

    b.local.pos.copy(ptran.pos);

    return this;
  }

  setFKQuat(v: Array<number>): this {
    const pose = this.rig.wpose;

    const selKey = posingStore.state.activeIKChain; // Which IK Selector?
    const sel = IK_SELECTORS[selKey]; // Get Selector Info
    const ch = this.rig[sel.chainKey].chain; // Get IK Chain for the selector
    const b = pose.bones[ch.links[0].index]; // Get the first bone of the chain in the working pose

    // Convert world space position to local space
    const ptran = pose.getWorldTransform(b.pindex);
    ptran.rot.invert().mul(v);

    b.local.rot.copy(ptran.rot);

    return this;
  }

  getTwist(idx: number = 1): number {
    return idx === 0
      ? this.rig.spine.target.twistRadA
      : this.rig.spine.target.twistRadB;
  }
  // #endregion

  // #region RIG

  /** Run IK Solvers & Update Character Skeleton */
  resolve() {
    this.rig.runSolvers();
    this.refreshSkeleton();
  }

  /** Refresh skeleton, init targets & selector rendering */
  refresh(doTargetRecalc: boolean = false) {
    // Apply pose to skeleton
    this.refreshSkeleton();

    // Reset all the IK Targets & Poles
    if (doTargetRecalc) {
      this.rig.initTargets();
    }

    // Update renders
    this.selectors.render();
    this.targets.render();
  }

  /** Update character skeleton with the IK Rig's working pose */
  refreshSkeleton(): this {
    applyPoseToSkeleton(this.rig.wpose, this.skel, ALLOW_POS_JOINTS);
    return this;
  }

  resetPose() {
    posingStore.clearActiveIKChain();

    this.rig.wpose.copy(this.rig.tpose);

    const bl = this.rig.wpose.bones[0].local;
    bl.rot.rotX(Math.PI * 0.5);
    bl.pos.copy([bl.pos[0], -bl.pos[2], bl.pos[1]]); // [x,-z,y] - X 90deg Shorthand

    this.pSystem.poseToHistory();
    this.pSystem.stateMachine.getCurrent()?.onUndoRedo();

    this.refresh(true);
    this.pSystem.fitViewPose(this.rig.wpose);
  }
  // #endregion

  // #region BVH

  exportBVHPose(props?: {
    rotationOffsets?: Record<string, [number?, number?, number?]>;
    channelOrder?: EulerOrder;
    includePosList?: string[];
    transformToZup?: boolean;
  }): string {
    const opts = { transformToZup: false, ...props };

    // opts.transformToZup = false;

    // if (!opts.transformToZup) {
    return poseFromSkeleton(this.skelBind, this.skel, opts);
    // }

    // // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // // Take extra steps to transform pose so Z is up

    // // Copy current pose to scratch skeleton
    // for (let i = 0; i < this.skel.bones.length; i++) {
    //   this.xSkel.bones[i].quaternion.copy(this.skel.bones[i].quaternion);
    //   this.xSkel.bones[i].position.copy(this.skel.bones[i].position);
    // }

    // // Rotate pose by +90 Deg X rotation, Yup to Zup
    // this.xSkel.bones[0].quaternion.premultiply(QUAT_3JS_X90);

    // // Bindpose should already be prepared with the transformation
    // return poseFromSkeleton(this.xSkelBind, this.xSkel, opts);
  }

  // #endregion

  // #region STATE HANDLERS
  onActiveChainChange = () => {
    // Update Visuals
    this.targets.render();
    this.selectors.render();

    // Reset stateMachine
    this.pSystem.stateMachine.clear();

    // Misc
    if (posingStore.state.activeIKChain !== IKChainType.None) {
      const sel = IK_SELECTORS[posingStore.state.activeIKChain];
      this.pSystem.stateMachine.push(sel.stateMachine);
    } else {
      this.pSystem.gizmo.hide();
    }
  };

  onStaticHand = () => {
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    const hPose = sceneSettingsStore.state.characterSettings.staticHands;
    const hBones = QUATERNIONS_BY_OPTION[hPose];
    if (!hBones) {
      console.warn(
        `${hPose} is not yet implemented or is an invalid hand option`,
      );
      return;
    }

    let prefix = '';
    switch (posingStore.state.activeIKChain) {
      case IKChainType.LeftArm:
        prefix = 'left_';
        break;

      case IKChainType.RightArm:
        prefix = 'right_';
        break;
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    const pose = this.rig.wpose;
    let bone: any;
    for (const [bName, q] of Object.entries(hBones)) {
      // Filter out bone names
      switch (bName) {
        case 'right_default':
        case 'left_default':
          continue;
      }

      // Filter out specific hand if an IK Chain is active
      if (prefix !== '' && !bName.startsWith(prefix)) continue;

      // Get bone object & apply rotation
      bone = pose.getBone(bName);
      if (!bone) {
        console.warn(`Error loading hand pose, bone not found : ${bName}`);
        continue;
      }

      bone.local.rot.copy(q);
    }

    this.refreshSkeleton();
    this.pSystem.poseToHistory();

    IS_DEV_ENV && console.log('Poser - Apply Hand Pose : ', prefix);
  };
  // #endregion

  // #region RENDERING

  // Render the axes of each chain
  renderAxes() {
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // JIT Mesh Creation
    const meshes = traverseFind<UnitAxesMesh>(
      this.pSystem.grpRenderGen,
      true,
      (o: any) => o.isUnitAxesMesh,
    );

    if (meshes.length === 0) {
      const cnt = Object.keys(IK_SELECTORS).length;
      const axes = new UnitAxesMesh().forDebugging();
      axes.scale.setScalar(0.2);
      meshes.push(axes);

      for (let i = 1; i < cnt; i++) meshes.push(axes.shallowClone());

      this.pSystem.grpRenderGen.add(...meshes);
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Update Axes Mesh
    let pos: Array<number>;
    let rot: Array<number>;
    let i = 0;
    for (const [k, v] of Object.entries(IK_SELECTORS)) {
      pos = this.rig.getTargetPos(v.chainKey);
      rot = this.rig.getChainOrientation(v.chainKey, -1);

      meshes[i].position.fromArray(pos);
      meshes[i].quaternion.fromArray(rot);
      meshes[i].visible = true;
      i++;
    }
  }

  // #endregion
}

// #region HELPERS
class RigSelectors {
  mesh: RingQuads; // Visualize Selector Points
  items: Array<any> = [];
  poser: Poser;
  #selIndex = 1;

  constructor(poser: Poser) {
    this.poser = poser;
    this.mesh = new RingQuads();
    this.mesh.renderOrder = 100;
    this.mesh.material.depthTest = false;
  }

  set selectedIndex(i: number) {
    this.#selIndex = i;

    if (i === -1) {
      posingStore.clearActiveIKChain();
    } else {
      const sel = this.items[i];
      if (sel) posingStore.state.activeIKChain = sel.type;
    }
  }

  dispose() {
    disposeObject3D(this.mesh);
  }

  clear() {
    this.items.length = 0;
    this.mesh.reset()._updateGeometry();
  }

  render() {
    const selKey = posingStore.state.activeIKChain;
    let pos: Array<number>;

    this.mesh.reset();
    this.items.length = 0;

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    for (const [k, v] of Object.entries(IK_SELECTORS)) {
      if (k === selKey) continue; // Skip selected chain

      pos = this.poser.rig.getTargetPos(v.chainKey);

      this.items.push(v);
      this.mesh.push(pos, 0xaa00aa, 0xffffff, 1.1, 0.5);
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Auto updating causes a flicker every so often
    // forcing gpu update now makes the flicker not show
    this.mesh._updateGeometry();
  }
}

class RigTargets {
  mesh: RingQuads; // Visualize Selector Points
  items: Array<any> = [];
  poser: Poser;
  #selIndex = -1;

  constructor(poser: Poser) {
    this.poser = poser;
    this.mesh = new RingQuads();
    this.mesh.renderOrder = 101;
    this.mesh.material.depthTest = false;
  }

  get selectedIndex(): number {
    return this.#selIndex;
  }

  set selectedIndex(i: number) {
    this.#selIndex = i;
    this.render();
  }

  setItems(itm: Array<any>) {
    this.items.length = 0;
    this.items.push(...itm);
    return this;
  }

  dispose() {
    disposeObject3D(this.mesh);
  }

  clear() {
    this.items.length = 0;
    this.#selIndex = -1;
    this.mesh.reset()._updateGeometry();
  }

  render() {
    const size = 1.5;
    const thick = 0.5;
    this.mesh.reset();

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    for (const [i, o] of this.items.entries()) {
      if (i === this.#selIndex) {
        // NOTE: Point is needed to keep indexing predictable
        // but making it small is another way to hide it
        this.mesh.push(o.pos, 0xffffff, 0xffffff, 0.1, thick);
      } else {
        switch (o.type) {
          case 'target':
            this.mesh.push(o.pos, 0x00aaaa, 0xffffff, size, thick);
            break;
          case 'ctrl':
            this.mesh.push(o.pos, 0xffaa00, 0xffffff, size, thick);
            break;
        }
      }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    this.mesh._updateGeometry();
    return this;
  }
}

// #endregion
